import React, { useState, useEffect, useMemo } from "react";
import MainModal from "components/ModalMain";
import Button from "components/Button";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import DatePicker from "components/DatePicker";
import Checkbox from "components/Checkbox";
import TextArea from "components/TextArea";
import TextTooltip from "./TextTooltip"
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_RATE_PLAN, GET_ALL_ROOM_TYPES } from "graphql/service";
import { Head, Container, InputRow, SettingLink, Footer } from "./styles";
import colors from "constants/colors";
import moment from "moment-timezone";
import { TIME_ZONE } from "constants/constant";
import { debouncedErrorMessage } from "utils/messageUtil";

const ModalAddRatePlan = ({
  hidden,
  buttonStyle,
  setRatePlans
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [ratePlanCode, setRatePlanCode] = useState("");
  const [ratePlanCodeError, setRatePlanCodeError] = useState(false);

  const [ratePlanType, setRatePlanType] = useState("Flat Amount Off ($)");

  const [discountAmount, setDiscountAmount] = useState("");
  const [discountAmountError, setDiscountAmountError] = useState(false);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);

  const [cancellationPolicy, setCancellationPolicy] = useState(
    "48-Hour Policy"
  );

  const [earliestCheckIn, setEarliestCheckIn] = useState(
    moment().tz(TIME_ZONE)
  );
  const [latestCheckOut, setLatestCheckOut] = useState(
    moment().add(1, "days").tz(TIME_ZONE)
  );
  const [ratePlanStart, setRatePlanStart] = useState(moment().tz(TIME_ZONE));
  const [ratePlanExpiry, setRatePlanExpiry] = useState(
    moment().add(1, "days").tz(TIME_ZONE)
  );

  const [ratePlanExpires, setRatePlanExpires] = useState(true);

  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState(false);

  const {data: roomTypeData, error} = useQuery(GET_ALL_ROOM_TYPES);

  const roomTypes = useMemo(() => {
    return roomTypeData?.roomTypes
      ?.filter((roomType) => roomType.name !== "Amenities")
      .map((roomType) => ({
        label: roomType.name,
        value: roomType.roomTypeId,
      })) ?? [];
  }, [roomTypeData]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const [createRatePlan, { loading }] = useMutation(CREATE_RATE_PLAN, {
    onCompleted: (data) => {
      setIsOpen(false);
      setRatePlans((prevRatePlans) => [...prevRatePlans, data.createRatePlan]);
    },
  });

  const handleAddRatePlan = () => {
    createRatePlan({
      variables: {
        //TODO: change to actual variables
      },
    });
  };

  return (
    <>
      <MainModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        className="modal-success"
        buttonLabel="Add Rate Plan"
        buttonStyle={buttonStyle}
        hidden={hidden}
        additionalstyle={`
          max-width: 968px;
          .modal-content{
            .close {
              z-index: 200;
            }          
            .modal-body {
              height: 100%;
              padding: 24px;
            }
          }
          top: 69px;
        `}
      >
        <Head>Add Rate Plan</Head>
        <Container>
          <InputRow>
            <Input
              title="RATE PLAN CODE"
              required
              error={ratePlanCodeError}
              errorMessage="Please enter a valid code"
              value={ratePlanCode}
              onChange={(e) => {
                setRatePlanCode(e.target.value);
                setRatePlanCodeError(false);
              }}
              additionalstyle={`
                width: calc(25% - 12px);

                input {
                  padding-left: 22px;
                }

                .warning {
                  white-space: nowrap;
                }
              `}
            />
            <Dropdown
              title="RATE PLAN TYPE"
              options={[
                "Flat Amount Off ($)",
                "Percentage Off (%)",
                "Fixed Price Per Night",
              ]}
              value={ratePlanType}
              onChange={(e) => setRatePlanType(e)}
              additionalstyle={`
                width: calc(25% - 12px);
              `}
            />
            <Input
              title="DISCOUNT"
              error={discountAmountError}
              errorMessage="Please enter a valid amount"
              value={discountAmount}
              onChange={(e) => {
                setDiscountAmount(e.target.value);
                setDiscountAmountError(false);
              }}
              additionalstyle={`
                width: calc(25% - 12px);

                input {
                  padding-left: 22px;
                }

                ::before {
                  content: '$';
                  font-size: 16px;
                  font-weight: 600;
                  position: absolute;
                  color: ${colors.white};
                  left: 12px;
                  top: 32px;
                }

                .warning {
                  white-space: nowrap;
                }
              `}
            />
          </InputRow>
          <InputRow>
            <Dropdown
              title="ROOM TYPE"
              required
              multiSelection
              options={roomTypes}
              value={selectedRoomTypes}
              onChange={(e) => {
                const newRoomTypes = [...selectedRoomTypes];
                if (newRoomTypes.includes(e)) {
                  const index = newRoomTypes.indexOf(e);
                  newRoomTypes.splice(index, 1);
                } else {
                  newRoomTypes.push(e);
                }
                setSelectedRoomTypes(newRoomTypes);
              }}
              additionalstyle={`
                width: calc(25% - 12px);
              `}
            />
            <Dropdown
              title="BOOKING & CANCELLATION POLICY"
              options={["48-Hour Policy"]}
              value={cancellationPolicy}
              onChange={(e) => setCancellationPolicy(e)}
              additionalstyle={`
                width: calc(25% - 12px);
              `}
            />
          </InputRow>
          <SettingLink
            to="/settings/hotel-info-and-policies"
            target="_blank"
            rel="noopener noreferrer"
            additionalstyle={`
              margin-left: calc(25% + 4px);
            `}
          >
            Manage Cancellation Policies
          </SettingLink>
          <InputRow>
            <div className="datePickerContainer">
              <span>EARLIEST CHECK-IN DATE</span>
              <DatePicker
                minDate={earliestCheckIn}
                dateValue={earliestCheckIn}
                dateOnChange={setEarliestCheckIn}
                additionalstyle={`
                    width: 100%;
                    min-width: 130px;
                    border: 1px solid ${colors.darkGrey};

                    input {
                      padding-right: 26px;
                    }
                  `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
            <div className="datePickerContainer">
              <span>LATEST CHECK-OUT DATE</span>
              <DatePicker
                disabled={!ratePlanExpires}
                minDate={latestCheckOut}
                dateValue={latestCheckOut}
                dateOnChange={setLatestCheckOut}
                additionalstyle={`
                    width: 100%;
                    min-width: 130px;
                    border: 1px solid ${colors.darkGrey};

                    input {
                      padding-right: 26px;
                    }

                    ${
                      !ratePlanExpires
                        ? `
                      border: none;

                      input {
                        padding-left: 0;
                      }

                      button {
                        display: none;
                      }
                    `
                        : null
                    }
                  `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
            <div className="datePickerContainer">
              <span>
                RATE PLAN START
                <TextTooltip
                  text={"This rate plan is no longer available for new bookings created any time after this end date."}
                />
              </span>
              <DatePicker
                minDate={ratePlanStart}
                dateValue={ratePlanStart}
                dateOnChange={setRatePlanStart}
                additionalstyle={`
                    width: 100%;
                    min-width: 130px;
                    border: 1px solid ${colors.darkGrey};

                    input {
                      padding-right: 26px;
                    }
                  `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
            <div className="datePickerContainer">
              <span>
                RATE PLAN EXPIRY 
                <TextTooltip
                  text={"This rate plan is available for new bookings created any time after this start date."}
                />
              </span>
              <DatePicker
                disabled={!ratePlanExpires}
                minDate={ratePlanExpiry}
                dateValue={ratePlanExpiry}
                dateOnChange={setRatePlanExpiry}
                additionalstyle={`
                    width: 100%;
                    min-width: 130px;
                    border: 1px solid ${colors.darkGrey};

                    input {
                      padding-right: 26px;
                    }

                    ${
                      !ratePlanExpires
                        ? `
                      border: none;

                      input {
                        padding-left: 0;
                      }

                      button {
                        display: none;
                      }
                    `
                        : null
                    }
                  `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
          </InputRow>
          <Checkbox
            checked={!ratePlanExpires}
            onChange={() => {
              setRatePlanExpires(!ratePlanExpires);
            }}
            title={"Rate plan does not expire"}
            additionalstyle={`
              margin-left: calc(75% + 16px);
            `}
          />
          <TextArea
            title="DESCRIPTION"
            required
            rows={4}
            error={descriptionError}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDescriptionError(false);
            }}
            additionalstyle={`
              flex-basis: 100%;
            `}
          />
        </Container>
        <Footer>
          <Button
            success
            additionalstyle={`
              width: 84px;
              margin-top: 25px;
            `}
            onClick={handleAddRatePlan}
            data-loading={loading}
          >
            {loading ? null : "Submit"}
          </Button>
        </Footer>
      </MainModal>
    </>
  );
};

export default ModalAddRatePlan;
